import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Grid,
  Box,
  Chip,
  Divider,
  Button,
  Card,
  CardContent,
  Avatar,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  IconButton,
  Link,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Badge,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SourceIcon from '@mui/icons-material/Source';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../redux/store';
import { updateLead as updateLeadRedux, deleteLead } from '../redux/slices/leadSlice';
import { updateLead as updateLeadService } from '../services/mockData';
import LeadInteractions from '../components/LeadInteractions';
import { useCreateNotification } from '../hooks/useCreateNotification';
import { NOTIFICATION_TYPES } from '../utils/notificationUtils';

const statusColors: Record<string, "info" | "warning" | "primary" | "success" | "error"> = {
  "Novo": 'info',
  "Contatado": 'warning',
  "Qualificado": 'primary',
  "Convertido": 'success',
  "Perdido": 'error',
};

interface LeadFormData {
  name: string;
  email: string;
  phone: string;
  source: string;
  status: string;
  notes: string;
  productInterest: string;
}

const LeadProfilePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const createNotification = useCreateNotification();
  
  const leads = useSelector((state: RootState) => state.leads.items);
  const products = useSelector((state: RootState) => state.products.items);
  
  const [lead, setLead] = useState<any | null>(null);
  const [product, setProduct] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  
  const [formData, setFormData] = useState<LeadFormData>({
    name: '',
    email: '',
    phone: '',
    source: '',
    status: '',
    notes: '',
    productInterest: '',
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (leads.length > 0 && id) {
      const foundLead = leads.find(l => l.id === id);
      if (foundLead) {
        console.log('Lead encontrado:', foundLead.id);
        console.log('Avatar do lead:', foundLead.avatar ? 'Sim' : 'Não');
        if (foundLead.avatar) {
          console.log('Avatar do lead (primeiros 50 caracteres):', foundLead.avatar.substring(0, 50) + '...');
        }
        setLead(foundLead);
        
        if (foundLead.productInterest) {
          const foundProduct = products.find(p => p.id === foundLead.productInterest);
          setProduct(foundProduct || null);
        }
        
        setFormData({
          name: foundLead.name || '',
          email: foundLead.email || '',
          phone: foundLead.phone || '',
          source: foundLead.source || '',
          status: foundLead.status || '',
          notes: foundLead.notes || '',
          productInterest: foundLead.productInterest || '',
        });
      } else {
        // Lead não encontrado, redirecionar para a página de leads
        navigate('/leads');
      }
    }
    setLoading(false);
  }, [id, leads, products, navigate]);

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('pt-BR');
    } catch (error) {
      return '-';
    }
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSourceChange = (event: SelectChangeEvent) => {
    setFormData({
      ...formData,
      source: event.target.value,
    });
  };

  const handleStatusChange = async (event: SelectChangeEvent) => {
    if (!lead) return;
    
    try {
      const newStatus = event.target.value;
      
      // Atualiza no localStorage primeiro
      const updatedLead = await updateLeadService(lead.id, { 
        status: newStatus,
        lastContactDate: new Date().toISOString()
      });
      
      // Atualiza no Redux
      dispatch(updateLeadRedux(updatedLead));
      
      // Atualiza o estado local
      setLead(updatedLead);
      setFormData(prev => ({
        ...prev,
        status: newStatus
      }));
      
      setSaveSuccess(true);
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      alert('Erro ao atualizar o status. Por favor, tente novamente.');
    }
  };

  const handleProductInterestChange = async (event: SelectChangeEvent) => {
    if (!lead) return;
    
    try {
      const productId = event.target.value;
      const selectedProduct = products.find(p => p.id === productId);
      
      // Atualiza no localStorage primeiro
      const updatedLead = await updateLeadService(lead.id, { 
        productInterest: productId,
        lastContactDate: new Date().toISOString()
      });
      
      // Atualiza no Redux
      dispatch(updateLeadRedux(updatedLead));
      
      // Atualiza os estados locais
      setLead(updatedLead);
      setProduct(selectedProduct || null);
      setFormData(prev => ({
        ...prev,
        productInterest: productId
      }));
      
      setSaveSuccess(true);
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Erro ao atualizar produto de interesse:', error);
      alert('Erro ao atualizar o produto de interesse. Por favor, tente novamente.');
    }
  };

  const handleSave = async () => {
    if (!lead) return;
    
    try {
      // Prepara os dados atualizados
      const updatedData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        source: formData.source,
        status: formData.status,
        notes: formData.notes,
        productInterest: formData.productInterest || undefined,
        lastContactDate: new Date().toISOString(),
        avatar: lead.avatar // Mantém o avatar atual
      };
      
      // Atualiza no localStorage primeiro
      const updatedLead = await updateLeadService(lead.id, updatedData);
      
      // Atualiza no Redux
      dispatch(updateLeadRedux(updatedLead));
      
      // Atualiza o estado local
      setLead(updatedLead);
      setEditing(false);
      setSaveSuccess(true);

      // Criar notificação
      createNotification({
        ...NOTIFICATION_TYPES.LEAD.UPDATED,
        message: `Lead atualizado: ${updatedLead.name}`,
        link: `/leads/${updatedLead.id}`,
        linkText: 'Ver lead'
      });
      
      // Limpa a mensagem de sucesso após 3 segundos
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Erro ao salvar lead:', error);
      setSaveSuccess(false);
      createNotification({
        ...NOTIFICATION_TYPES.LEAD.ERROR,
        message: 'Erro ao atualizar lead. Por favor, tente novamente.'
      });
    }
  };

  const handleCancel = () => {
    // Restaurar dados originais
    if (lead) {
      setFormData({
        name: lead.name || '',
        email: lead.email || '',
        phone: lead.phone || '',
        source: lead.source || '',
        status: lead.status || '',
        notes: lead.notes || '',
        productInterest: lead.productInterest || '',
      });
    }
    setEditing(false);
  };

  const handleOpenDeleteDialog = () => {
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const handleDeleteLead = () => {
    if (!id) return;
    
    try {
      dispatch(deleteLead(id));
      
      // Criar notificação
      createNotification({
        ...NOTIFICATION_TYPES.LEAD.DELETED,
        message: `Lead excluído: ${lead?.name || 'Lead'}`,
        link: '/leads',
        linkText: 'Ver leads'
      });

      handleCloseDeleteDialog();
      navigate('/leads');
    } catch (error) {
      console.error('Erro ao excluir lead:', error);
      createNotification({
        ...NOTIFICATION_TYPES.LEAD.ERROR,
        message: 'Erro ao excluir lead. Por favor, tente novamente.'
      });
    }
  };

  const handlePhotoClick = () => {
    fileInputRef.current?.click();
  };

  const handlePhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const newAvatar = reader.result as string;
        
        if (lead) {
          try {
            // Reduz o tamanho da imagem se necessário
            let processedAvatar = newAvatar;
            
            // Se a imagem for muito grande (mais de 500KB), tenta reduzir
            if (newAvatar.length > 500000) {
              console.log('Imagem muito grande, tentando reduzir...');
              
              // Cria uma imagem temporária para redimensionar
              const img = new Image();
              img.src = newAvatar;
              
              // Aguarda o carregamento da imagem
              await new Promise((resolve) => {
                img.onload = resolve;
              });
              
              // Cria um canvas para redimensionar
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');
              
              // Define o tamanho máximo
              const maxWidth = 300;
              const maxHeight = 300;
              
              // Calcula as novas dimensões mantendo a proporção
              let width = img.width;
              let height = img.height;
              
              if (width > height) {
                if (width > maxWidth) {
                  height = Math.round((height * maxWidth) / width);
                  width = maxWidth;
                }
              } else {
                if (height > maxHeight) {
                  width = Math.round((width * maxHeight) / height);
                  height = maxHeight;
                }
              }
              
              // Configura o canvas
              canvas.width = width;
              canvas.height = height;
              
              // Desenha a imagem redimensionada
              ctx?.drawImage(img, 0, 0, width, height);
              
              // Converte para base64 com qualidade reduzida
              processedAvatar = canvas.toDataURL('image/jpeg', 0.7);
              
              console.log(`Imagem redimensionada: ${Math.round(processedAvatar.length / 1024)}KB`);
            }
            
            // Atualiza no localStorage primeiro
            const updatedLead = await updateLeadService(lead.id, { 
              avatar: processedAvatar,
              lastContactDate: new Date().toISOString()
            });
            
            // Atualiza no Redux
            dispatch(updateLeadRedux(updatedLead));
            
            // Atualiza o estado local
            setLead(updatedLead);
            
            setSaveSuccess(true);
            setTimeout(() => {
              setSaveSuccess(false);
            }, 3000);
          } catch (error) {
            console.error('Erro ao salvar foto:', error);
            setSaveSuccess(false);
            alert('Erro ao salvar a foto. Por favor, tente novamente.');
          }
        }
      };
      reader.readAsDataURL(file);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" my={8}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!lead) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h5" color="error">
          Lead não encontrado
        </Typography>
        <Button 
          component={RouterLink} 
          to="/leads" 
          variant="contained" 
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
        >
          Voltar para Leads
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton 
          component={RouterLink} 
          to="/leads" 
          sx={{ mr: 2 }}
          aria-label="voltar"
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          Perfil do Lead
        </Typography>
      </Box>

      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Dados do lead atualizados com sucesso!
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handlePhotoChange}
                />
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    badgeContent={
                      <IconButton
                        onClick={handlePhotoClick}
                        sx={{
                          bgcolor: 'primary.main',
                          color: 'white',
                          '&:hover': {
                            bgcolor: 'primary.dark',
                          },
                          width: 32,
                          height: 32,
                        }}
                      >
                        <PhotoCameraIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    }
                  >
                    <Avatar 
                      src={lead.avatar === 'AVATAR_REMOVED' ? undefined : lead.avatar}
                      sx={{ 
                        width: 100, 
                        height: 100, 
                        fontSize: 40,
                        bgcolor: 'primary.main',
                        mb: 2
                      }}
                    >
                      {lead.name.charAt(0).toUpperCase()}
                    </Avatar>
                  </Badge>
                </Box>
                
                {editing ? (
                  <TextField
                    name="name"
                    label="Nome"
                    fullWidth
                    value={formData.name}
                    onChange={handleTextChange}
                    margin="normal"
                  />
                ) : (
                  <Typography variant="h5" align="center" gutterBottom>
                    {lead.name}
                  </Typography>
                )}
                
                <Chip 
                  label={editing ? formData.status : lead.status} 
                  color={statusColors[editing ? formData.status : lead.status] || 'default'} 
                  sx={{ mt: 1 }}
                />
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ '& > div': { mb: 2 } }}>
                <Box display="flex" alignItems="center">
                  <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  {editing ? (
                    <TextField
                      name="email"
                      label="Email"
                      fullWidth
                      value={formData.email}
                      onChange={handleTextChange}
                      size="small"
                    />
                  ) : (
                    <Typography variant="body1">{lead.email}</Typography>
                  )}
                </Box>
                
                <Box display="flex" alignItems="center">
                  <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  {editing ? (
                    <TextField
                      name="phone"
                      label="Telefone"
                      fullWidth
                      value={formData.phone}
                      onChange={handleTextChange}
                      size="small"
                    />
                  ) : (
                    <Typography variant="body1">{lead.phone || '-'}</Typography>
                  )}
                </Box>
                
                <Box display="flex" alignItems="center">
                  <SourceIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  {editing ? (
                    <FormControl fullWidth size="small">
                      <InputLabel id="source-label">Origem</InputLabel>
                      <Select
                        labelId="source-label"
                        value={formData.source}
                        onChange={handleSourceChange}
                        label="Origem"
                      >
                        <MenuItem value="Site">Site</MenuItem>
                        <MenuItem value="Facebook">Facebook</MenuItem>
                        <MenuItem value="Instagram">Instagram</MenuItem>
                        <MenuItem value="LinkedIn">LinkedIn</MenuItem>
                        <MenuItem value="Twitter">Twitter</MenuItem>
                        <MenuItem value="WhatsApp">WhatsApp</MenuItem>
                        <MenuItem value="Indicação">Indicação</MenuItem>
                        <MenuItem value="Importação">Importação</MenuItem>
                        <MenuItem value="Outro">Outro</MenuItem>
                      </Select>
                    </FormControl>
                  ) : (
                    <Typography variant="body1">Origem: {lead.source}</Typography>
                  )}
                </Box>
                
                <Box display="flex" alignItems="center">
                  <CalendarTodayIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body1">
                    Último contato: {formatDate(lead.lastContactDate)}
                  </Typography>
                </Box>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Box display="flex" justifyContent="center">
                {editing ? (
                  <Box display="flex" gap={1}>
                    <Button 
                      variant="contained" 
                      color="primary" 
                      startIcon={<SaveIcon />}
                      onClick={handleSave}
                    >
                      Salvar
                    </Button>
                    <Button 
                      variant="outlined" 
                      color="secondary" 
                      startIcon={<CancelIcon />}
                      onClick={handleCancel}
                    >
                      Cancelar
                    </Button>
                  </Box>
                ) : (
                  <Box display="flex" gap={1}>
                    <Button 
                      variant="contained" 
                      startIcon={<EditIcon />}
                      onClick={() => setEditing(true)}
                    >
                      Editar Lead
                    </Button>
                    <Button 
                      variant="outlined" 
                      color="error" 
                      startIcon={<DeleteIcon />}
                      onClick={handleOpenDeleteDialog}
                    >
                      Deletar Lead
                    </Button>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={8}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Status do Lead
                </Typography>
                
                {editing ? (
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="status-label">Status</InputLabel>
                    <Select
                      labelId="status-label"
                      value={formData.status}
                      onChange={handleStatusChange}
                      label="Status"
                    >
                      <MenuItem value="Novo">Novo</MenuItem>
                      <MenuItem value="Contatado">Contatado</MenuItem>
                      <MenuItem value="Qualificado">Qualificado</MenuItem>
                      <MenuItem value="Convertido">Convertido</MenuItem>
                      <MenuItem value="Perdido">Perdido</MenuItem>
                    </Select>
                  </FormControl>
                ) : (
                  <Box display="flex" alignItems="center" mt={2}>
                    <Chip 
                      label={lead.status} 
                      color={statusColors[lead.status] || 'default'} 
                      sx={{ mr: 2 }}
                    />
                    <Typography variant="body1">
                      Atualizado em: {formatDate(lead.lastContactDate)}
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
            
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Produto de Interesse
                </Typography>
                
                {editing ? (
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="product-interest-label">Produto de Interesse</InputLabel>
                    <Select
                      labelId="product-interest-label"
                      value={formData.productInterest}
                      onChange={handleProductInterestChange}
                      label="Produto de Interesse"
                    >
                      <MenuItem value="">Nenhum</MenuItem>
                      {products.map((p) => (
                        <MenuItem key={p.id} value={p.id}>
                          {p.name} - R$ {p.price.toFixed(2)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <Box mt={2}>
                    {product ? (
                      <Box>
                        <Link 
                          component={RouterLink} 
                          to={`/produtos/${product.id}`}
                          underline="hover"
                          color="primary"
                          sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}
                        >
                          {product.name}
                        </Link>
                        <Typography variant="body1" color="primary" sx={{ mt: 1 }}>
                          R$ {product.price.toFixed(2)}
                        </Typography>
                        {product.category && (
                          <Chip 
                            label={product.category} 
                            size="small" 
                            color="secondary" 
                            sx={{ mt: 1 }} 
                          />
                        )}
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {product.description.length > 200 
                            ? `${product.description.substring(0, 200)}...` 
                            : product.description}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body1" color="text.secondary">
                        Nenhum produto selecionado
                      </Typography>
                    )}
                  </Box>
                )}
              </Paper>
            </Grid>
            
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Observações
                </Typography>
                
                {editing ? (
                  <TextField
                    name="notes"
                    label="Observações"
                    fullWidth
                    multiline
                    rows={4}
                    value={formData.notes}
                    onChange={handleTextChange}
                    margin="normal"
                  />
                ) : (
                  <Typography variant="body1" sx={{ mt: 2, whiteSpace: 'pre-line' }}>
                    {lead.notes || 'Nenhuma observação registrada.'}
                  </Typography>
                )}
              </Paper>
            </Grid>
            
            <Grid item xs={12}>
              <LeadInteractions 
                leadId={lead.id} 
                interactions={lead.interactions || []} 
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Dialog de confirmação para deletar lead */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Confirmar exclusão
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Tem certeza que deseja excluir o lead <strong>{lead?.name}</strong>? Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            Cancelar
          </Button>
          <Button onClick={handleDeleteLead} color="error" variant="contained" autoFocus>
            Deletar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default LeadProfilePage; 